import { Button, DropdownButton } from 'lib/components';
import CustomDatePicker from 'lib/components/CustomDatePicker';
import React from 'react';
import ReactDom from 'react-dom';
import { useInvoicePagination } from '../../hooks';
import { DropdownItem } from '../../lib/components/types';
import { actions, useAppDispatch, useSelectHospitals, useSelectInvoices } from '../../store';
import classes from './index.module.scss';

/**
 * Props interface for the AccrueInvoiceModal component
 */
interface Props {
	/** Controls whether the modal is visible */
	isOpen: boolean;
	/** Callback function when modal is closed */
	onClose: () => any;
	/** Callback function when accrue action is confirmed with hospital ID */
	onAccrueInvoice: (hospitalId: number) => any;
	/** Start date for the invoice accrual period */
	startDate: Date;
	/** End date for the invoice accrual period */
	endDate: Date;
	/** Callback to update the start date */
	setStartDate: (date: Date) => any;
	/** Callback to update the end date */
	setEndDate: (date: Date) => any;
}

/**
 * AccrueInvoiceModal component
 * Modal dialog for accruing invoices for a specific hospital and date range
 * Allows selecting a hospital and displays the date range for accrual
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element | null} - Rendered component or null if not open
 */
const AccrueInvoiceModal = ({
	isOpen,
	onClose,
	onAccrueInvoice,
	startDate,
	endDate,
	setStartDate,
	setEndDate,
}: Props) => {
	// Redux state and actions
	const dispatch = useAppDispatch();
	const invoices = useSelectInvoices();
	const hospitals = useSelectHospitals();

	// Hospital dropdown state
	const [hospitalDropdownItems, setHospitalDropdownItems] = React.useState<DropdownItem<number>[]>([]);
	const [selectedHospitalDropdownItem, setSelectedHospitalDropdownItem] = React.useState<number>(-1);

	// Set up pagination for hospital data
	const { isDataFetched, hospitalListing } = useInvoicePagination(invoices, hospitals);

	/**
	 * Handle hospital dropdown selection
	 * Updates the selected hospital state
	 *
	 * @param {number} value - Selected hospital ID
	 */
	const handleHospitalDropdownItemSelect = (value: number) => {
		setSelectedHospitalDropdownItem(value);
	};

	/**
	 * Initialize hospital dropdown items when data is fetched
	 * Maps hospital data to dropdown format
	 */
	React.useEffect(() => {
		if (isDataFetched) {
			setHospitalDropdownItems(() => hospitalListing.map(({ id, name }) => ({ label: name, value: id })));
		}
	}, [isDataFetched]);

	/**
	 * Fetch hospitals data on component mount
	 * Note: Fetching invoices is commented out as it's likely handled by the parent component
	 */
	React.useEffect(() => {
		// dispatch(actions.fetchInvoices());
		dispatch(actions.fetchHospitals());
	}, []);

	// Don't render anything if modal is not open
	if (!isOpen) {
		return null;
	}

	// Use React Portal to render the modal outside the normal DOM hierarchy
	return ReactDom.createPortal(
		<div className={classes['modal']}>
			<div className={classes['modal-content']}>
				<div className={classes['modal-items']}>
					{/* Form with date pickers and hospital dropdown */}
					<div className={classes['modal-form']}>
						{/* Date range selection */}
						<div className={classes['modal-fields']}>
							<CustomDatePicker label="From" value={startDate} onChange={setStartDate} />
							<CustomDatePicker label="To" value={endDate} onChange={setEndDate} />
						</div>
						{/* Hospital selection */}
						<div className={classes['modal-fields']}>
							<div style={{ marginTop: '1rem', marginRight: '0.5rem' }}>
								<DropdownButton
									css={{ width: '100%' }}
									title="Hospital"
									list={hospitalDropdownItems}
									onItemSelect={handleHospitalDropdownItemSelect}
								/>
							</div>
						</div>
						{/* Commented out helper text - could be used to show count of invoices */}
						{/* <Typography className={classes['modal-fields-helperText']}>
							13 Invoices Found between those dates
						</Typography> */}
					</div>
					{/* Footer with action buttons */}
					<div className={classes['modal-footer']}>
						{/* Confirm button - disabled until a hospital is selected */}
						<Button
							disabled={selectedHospitalDropdownItem === -1}
							variant="standard"
							color="primary"
							title="Accrue Invoice"
							style={{
								marginRight: '1rem',
								justifyContent: 'center',
								flexBasis: '50%',
							}}
							onClick={() => {
								onAccrueInvoice(selectedHospitalDropdownItem);
								onClose();
							}}
						/>
						{/* Cancel button */}
						<Button variant="standard" color="gray" sizing="small" title="Cancel" onClick={onClose} />
					</div>
				</div>
			</div>
		</div>,
		document.getElementById('portal') as HTMLElement
	);
};

export default AccrueInvoiceModal;
